import { EOLIngestionStatus, LeasingDocs } from '@/models/docs';

import {
    OcrProgress,
    ProgressPhase,
    RawOcrProgress,
    computeOcrProgress,
} from '@/lib/types';

import { IngestionWarning } from '../end-of-lease/types';

export { computeOcrProgress };
export type { OcrProgress, ProgressPhase, RawOcrProgress };

export type DocumentSchema = LeasingDocs;

export interface PaginatedDocumentsSchema {
    total: number;
    results: DocumentSchema[];
}

export type FileStatus = 'ready' | 'processing' | 'error';

export type IngestionStatusFilter = 'all' | 'success' | 'processing' | 'error';

export type DocumentIngestionStatus = EOLIngestionStatus | null;

/**
 * Best-effort ingestion stage (spike).
 *
 * `validated` — fully extracted & canonical; `partial` — useful data but
 * incomplete (e.g. missing required fields); `unusable` — no meaningful data.
 */
export type BestEffortState = 'validated' | 'partial' | 'unusable';

export interface BestEffort {
    finalState: BestEffortState;
    actionable: boolean | null;
    missingRequiredFields: string[];
}

export interface DocumentSummary {
    id: number;
    type: string;
    /** M4's IATA binder folder code; absent on documents the legacy classifier typed. */
    folderCode?: string | null;
    canonicalId: string | null;
    title: string | null;
    pageStart: number | null;
    pageEnd: number | null;
    issueDate: string | null;
    ingestionStatus: DocumentIngestionStatus;
    ingestionMessage: string | null;
    ingestionWarnings: IngestionWarning[];
    /** Present only for best-effort ingested docs (currently ADs behind the flag). */
    bestEffort?: BestEffort | null;
}

// Human-correction editor (best-effort spike)

export type ExtractionSource = 'vlm' | 'pydantic' | 'human';

export interface CorrectionFieldModel {
    name: string;
    description: string | null;
    value: unknown;
    source: ExtractionSource | null;
    missing: boolean;
    invalid: boolean;
    error: string | null;
    humanValue: unknown;
}

export interface CorrectionGroup {
    modelClass: string; // "ADHeader" | "ADContent"
    label: string;
    fields: CorrectionFieldModel[];
}

export interface CorrectionReference {
    type: string | null;
    id: string | null;
    revision: string | null;
}

export interface CorrectionView {
    docId: number;
    finalState: BestEffortState | null;
    actionable: boolean | null;
    missingRequiredFields: string[];
    groups: CorrectionGroup[];
    references: CorrectionReference[];
    invalidReferences: unknown[];
}

export interface SaveCorrectionsResponse {
    runId: string | null;
    mergeTriggered: boolean;
}

export interface FileWithDocuments {
    id: number;
    ingestedFileId: number | null;
    name: string;
    fileSize: number;
    createdAt: string;
    status: FileStatus;
    documentCount: number;
    errorMessage: string | null;
    processingStep: string | null;
    ocrProgress: RawOcrProgress | null;
    documents: DocumentSummary[];
    presignedUrl: string;
}

// Folder-tree view contract, mirroring the toCamelCase-converted GET /api/files/tree:
// a discriminated union on `type` where folders nest their entries in `children`.
export type StoredIn = {
    projectId: number | null;
    projectTitle: string | null;
    aircraftRegistration: string | null;
    aircraftMsn: string | null;
};

export interface FileNode extends FileWithDocuments {
    type: 'file';
    objectKey: string;
    relativePath: string | null;
    storedIn: StoredIn | null;
}

export interface FolderNode {
    type: 'folder';
    name: string;
    /** Unique per folder instance; the same relativePath can exist once per upload. */
    key: string;
    relativePath: string;
    totalSize: number;
    status: FileStatus;
    storedIn: StoredIn | null;
    children: RepositoryNode[];
}

export type RepositoryNode = FolderNode | FileNode;

export function isFileNode(node: RepositoryNode): node is FileNode {
    return node.type === 'file';
}

export function isFolderNode(node: RepositoryNode): node is FolderNode {
    return node.type === 'folder';
}

export interface PaginatedFilesTree {
    total: number;
    results: RepositoryNode[];
}

export interface DeleteFileResponse {
    deletedDocuments: number;
}

export type CheckboxStatus = 'checked' | 'unchecked' | 'n/a';

export interface TaskCardMaintenanceAction {
    itemNumber: string;
    taskTitle: string;
    compliance: string | null;
    reference: string | null;
}

export interface TaskCardWorkPerformed {
    subtaskNo: string;
    qualification: string;
    signatureId: string | null;
    signatureDate: string | null;
    details: string | null;
    duplicateInspection: boolean | null;
    etopsRequired: boolean | null;
}

export interface TaskCardDocumentReference {
    type: string;
    id: string;
    revision: string | null;
}

export interface TaskCardDocumentData {
    type: 'TaskCard';
    isEntryError: boolean;
    finishDate: string;
    totalPageCount: number | null;
    woNumber: string;
    woName: string | null;
    cardNumber: string;
    revisionNumber: string;
    area: string | null;
    headerDrNumber: string | null;
    customer: string | null;
    aircraftType: string | null;
    aircraftRegistration: string;
    effectivity: string | null;
    tcdAd: CheckboxStatus;
    datasheet: CheckboxStatus;
    dsmc: CheckboxStatus;
    cdccl: CheckboxStatus;
    repAlt: string | null;
    description: string;
    remarks: string | null;
    mroCompany: string | null;
    ammReferenceNo: string | null;
    ammReferenceDate: string | null;
    srmReferenceNo: string | null;
    srmReferenceDate: string | null;
    maintenanceActions: TaskCardMaintenanceAction[];
    workPerformedSignatures: TaskCardWorkPerformed[];
    documentReferences: TaskCardDocumentReference[];
}

export interface DiscrepancyReportDocumentData {
    type: 'DiscrepancyReport';
    isEntryError: boolean;
    reportNumber: string | null;
    ident: string | null;
    totalPageCount: number | null;
    startDate: string | null;
    finishDate: string | null;
    woNumber: string | null;
    nomenclature: string | null;
    regReference: string | null;
    taskPositionNumber: string | null;
    aircraftType: string | null;
    aircraftRegistration: string | null;
    operator: string | null;
    zone: string | null;
    customerOrder: string | null;
    offerNumber: string | null;
    zoneDescription: string | null;
    ataChapter: string | null;
    ammCustomer: string | null;
    ammReferenceNo: string | null;
    ammReferenceDate: string | null;
    srmReferenceNo: string | null;
    srmReferenceDate: string | null;
    discrepancyTitle: string | null;
    wpReference: string | null;
    discrepancyDescription: string | null;
    correctiveAction: string | null;
    workPerformedSignatures: TaskCardWorkPerformed[];
    documentReferences: TaskCardDocumentReference[];
}

// Camelcased response from POST /api/files/bulk-delete.
export interface BulkFileDeletionResult {
    deletedCount: number;
    alreadyDeletedCount: number;
    failed: { fileId: number; reason: string }[];
}

// Copy for the result dialog.
export interface BulkDeleteResultCopy {
    title: string; // dialog header, e.g. "Deletion complete" / "Server timeout"
    body: string; // main sentence
    note?: string; // optional second line (retry / contact-admin hint)
    buttonLabel: string;
}
